import { supabase } from "@/integrations/supabase/client";
import { UserHistoryEntry, UserIdeaEntry, HistoryEntry } from "@/types/history";
import { Idea } from "@/components/IdeaCard";

export interface HistoryService {
  getUserHistory: (userId: string) => Promise<HistoryEntry[]>;
  saveHistoryEntry: (userId: string, imageUrl: string, ideas: Idea[]) => Promise<string>;
  getUserSavedIdeas: (userId: string) => Promise<Idea[]>;
  saveIdea: (userId: string, idea: Idea, historyId?: string) => Promise<void>;
  removeSavedIdea: (userId: string, ideaId: string) => Promise<void>;
  clearAllSavedIdeas: (userId: string) => Promise<void>;
  deleteHistoryEntry: (userId: string, historyId: string) => Promise<void>;
}

export const historyService: HistoryService = {
  async getUserHistory(userId: string): Promise<HistoryEntry[]> {
    try {
      console.log(`📚 Fetching history for user: ${userId}`);
      
      // Fetch history entries with their associated ideas
      const { data: historyData, error: historyError } = await supabase
        .from('user_history')
        .select(`
          id,
          image_url,
          timestamp,
          user_ideas (
            id,
            title,
            description,
            difficulty,
            time_required,
            materials,
            instructions,
            storyline,
            image_url
          )
        `)
        .eq('user_id', userId)
        .order('timestamp', { ascending: false });

      if (historyError) {
        console.error('❌ Error fetching history:', historyError);
        throw historyError;
      }

      console.log(`✅ Fetched ${historyData?.length || 0} history entries`);

      // Transform data to match UI expectations
      const transformedHistory: HistoryEntry[] = (historyData || []).map(entry => ({
        id: entry.id,
        imageUrl: entry.image_url,
        timestamp: entry.timestamp,
        ideas: (entry.user_ideas || []).map(idea => ({
          id: idea.id,
          title: idea.title,
          description: idea.description,
          difficulty: idea.difficulty as 'easy' | 'medium' | 'hard',
          timeRequired: idea.time_required || '',
          materials: idea.materials || [],
          instructions: idea.instructions || [],
          storyline: idea.storyline,
          imageUrl: idea.image_url
        }))
      }));

      return transformedHistory;
    } catch (error) {
      console.error('❌ Error in getUserHistory:', error);
      return [];
    }
  },

  async saveHistoryEntry(userId: string, imageUrl: string, ideas: Idea[]): Promise<string> {
    try {
      console.log(`💾 Saving history entry for user: ${userId}`);

      // Insert history entry
      const { data: historyData, error: historyError } = await supabase
        .from('user_history')
        .insert({
          user_id: userId,
          image_url: imageUrl,
          timestamp: new Date().toISOString()
        })
        .select()
        .single();

      if (historyError) {
        console.error('❌ Error saving history:', historyError);
        throw historyError;
      }

      const historyId = historyData.id;

      // Insert associated ideas
      if (ideas.length > 0) {
        const { error: ideasError } = await supabase
          .from('user_ideas')
          .insert(
            ideas.map(idea => ({
              history_id: historyId,
              user_id: userId,
              title: idea.title,
              description: idea.description,
              difficulty: idea.difficulty,
              time_required: idea.timeRequired,
              materials: idea.materials,
              instructions: idea.instructions,
              storyline: idea.storyline,
              image_url: idea.imageUrl
            }))
          );

        if (ideasError) {
          console.error('❌ Error saving ideas:', ideasError);
          throw ideasError;
        }
      }

      console.log(`✅ Saved history entry with ID: ${historyId}`);
      return historyId;
    } catch (error) {
      console.error('❌ Error in saveHistoryEntry:', error);
      throw error;
    }
  },

  async getUserSavedIdeas(userId: string): Promise<Idea[]> {
    try {
      console.log(`📖 Fetching saved ideas for user: ${userId}`);

      const { data: savedIdeasData, error } = await supabase
        .from('user_saved_ideas')
        .select(`
          user_ideas (
            id,
            title,
            description,
            difficulty,
            time_required,
            materials,
            instructions,
            storyline,
            image_url
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching saved ideas:', error);
        throw error;
      }

      console.log(`✅ Fetched ${savedIdeasData?.length || 0} saved ideas`);

      // Transform data to match UI expectations
      const transformedIdeas: Idea[] = (savedIdeasData || [])
        .filter(item => item.user_ideas)
        .map(item => ({
          id: item.user_ideas!.id,
          title: item.user_ideas!.title,
          description: item.user_ideas!.description,
          difficulty: item.user_ideas!.difficulty as 'easy' | 'medium' | 'hard',
          timeRequired: item.user_ideas!.time_required || '',
          materials: item.user_ideas!.materials || [],
          instructions: item.user_ideas!.instructions || [],
          storyline: item.user_ideas!.storyline,
          imageUrl: item.user_ideas!.image_url
        }));

      return transformedIdeas;
    } catch (error) {
      console.error('❌ Error in getUserSavedIdeas:', error);
      return [];
    }
  },

  async saveIdea(userId: string, idea: Idea, historyId?: string): Promise<void> {
    try {
      console.log(`💾 Saving idea for user: ${userId}`, idea.title);

      // Check if this idea already exists in saved ideas
      const { data: existingData } = await supabase
        .from('user_saved_ideas')
        .select('id')
        .eq('user_id', userId)
        .eq('idea_id', idea.id);

      if (existingData && existingData.length > 0) {
        console.log('ℹ️ Idea already saved');
        return;
      }

      // Insert into saved ideas
      const { error } = await supabase
        .from('user_saved_ideas')
        .insert({
          user_id: userId,
          idea_id: idea.id
        });

      if (error) {
        console.error('❌ Error saving idea:', error);
        throw error;
      }

      console.log('✅ Idea saved successfully');
    } catch (error) {
      console.error('❌ Error in saveIdea:', error);
      throw error;
    }
  },

  async removeSavedIdea(userId: string, ideaId: string): Promise<void> {
    try {
      console.log(`🗑️ Removing saved idea for user: ${userId}`, ideaId);

      const { error } = await supabase
        .from('user_saved_ideas')
        .delete()
        .eq('user_id', userId)
        .eq('idea_id', ideaId);

      if (error) {
        console.error('❌ Error removing saved idea:', error);
        throw error;
      }

      console.log('✅ Saved idea removed successfully');
    } catch (error) {
      console.error('❌ Error in removeSavedIdea:', error);
      throw error;
    }
  },

  async clearAllSavedIdeas(userId: string): Promise<void> {
    try {
      console.log(`🗑️ Clearing all saved ideas for user: ${userId}`);

      const { error } = await supabase
        .from('user_saved_ideas')
        .delete()
        .eq('user_id', userId);

      if (error) {
        console.error('❌ Error clearing saved ideas:', error);
        throw error;
      }

      console.log('✅ All saved ideas cleared successfully');
    } catch (error) {
      console.error('❌ Error in clearAllSavedIdeas:', error);
      throw error;
    }
  },

  async deleteHistoryEntry(userId: string, historyId: string): Promise<void> {
    try {
      console.log(`🗑️ Deleting history entry for user: ${userId}, history: ${historyId}`);

      // Step 1: First get the idea IDs from this history entry
      const { data: ideaIds, error: ideaFetchError } = await supabase
        .from('user_ideas')
        .select('id')
        .eq('history_id', historyId)
        .eq('user_id', userId);

      if (ideaFetchError) {
        console.error('❌ Error fetching idea IDs:', ideaFetchError);
        throw ideaFetchError;
      }

      // Step 2: Delete saved ideas referencing these ideas
      if (ideaIds && ideaIds.length > 0) {
        const { error: savedIdeasError } = await supabase
          .from('user_saved_ideas')
          .delete()
          .eq('user_id', userId)
          .in('idea_id', ideaIds.map(idea => idea.id));

        if (savedIdeasError) {
          console.error('❌ Error deleting saved ideas:', savedIdeasError);
          throw savedIdeasError;
        }
      }

      // Step 3: Delete all ideas associated with this history entry
      const { error: ideasError } = await supabase
        .from('user_ideas')
        .delete()
        .eq('history_id', historyId)
        .eq('user_id', userId);

      if (ideasError) {
        console.error('❌ Error deleting ideas:', ideasError);
        throw ideasError;
      }

      // Step 4: Delete the history entry itself
      const { error: historyError } = await supabase
        .from('user_history')
        .delete()
        .eq('id', historyId)
        .eq('user_id', userId);

      if (historyError) {
        console.error('❌ Error deleting history entry:', historyError);
        throw historyError;
      }

      console.log('✅ History entry deleted successfully');
    } catch (error) {
      console.error('❌ Error in deleteHistoryEntry:', error);
      throw error;
    }
  }
};

// Migration utility to move localStorage data to database
export const migrateLocalStorageToDatabase = async (userId: string): Promise<void> => {
  try {
    console.log(`🔄 Starting migration for user: ${userId}`);

    // Check if localStorage has data
    const localHistory = localStorage.getItem('createJoyHistory');
    const localSavedIdeas = localStorage.getItem('savedIdeas');

    if (localHistory) {
      const historyEntries = JSON.parse(localHistory);
      console.log(`🔄 Migrating ${historyEntries.length} history entries`);

      for (const entry of historyEntries) {
        try {
          await historyService.saveHistoryEntry(userId, entry.imageUrl, entry.ideas);
        } catch (error) {
          console.error('❌ Error migrating history entry:', error);
        }
      }

      // Clear localStorage after successful migration
      localStorage.removeItem('createJoyHistory');
    }

    if (localSavedIdeas) {
      const savedIdeas = JSON.parse(localSavedIdeas);
      console.log(`🔄 Migrating ${savedIdeas.length} saved ideas`);

      for (const idea of savedIdeas) {
        try {
          await historyService.saveIdea(userId, idea);
        } catch (error) {
          console.error('❌ Error migrating saved idea:', error);
        }
      }

      // Clear localStorage after successful migration
      localStorage.removeItem('savedIdeas');
    }

    console.log('✅ Migration completed successfully');
  } catch (error) {
    console.error('❌ Error during migration:', error);
  }
};